import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	cleanCell4GFddLimenet,
	cleanCell4GTddLimenet,
	cleanCell5GLimenet,
	updateCell4GFddLimenet,
	updateCell4GTddLimenet,
	updateCell5GLimenet,
} from '@api/manageCellsLimenet';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IUpdateCellLimenetDTO, TCleanCellLimenetDTO } from '@interfaces/cells/dto/cellsDTO';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { ESnackbarTypes, ETechnologiesValues } from '@enums/common';

import {
	CROWDCELLS_CONFIG_DATA,
	REMOVE_CELL,
	UPDATE_CELL_4G_FDD,
	UPDATE_CELL_4G_TDD,
	UPDATE_CELL_5G,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseManageCellBySitesLimenetParams {
	technology?: string;
	closeCellModal: () => void;
	closeClearConfirm: () => void;
}

export const useManageCellsBySitesLimenet = (params: IUseManageCellBySitesLimenetParams) => {
	const dispatch = useAppDispatch();

	const {
		closeCellModal,
		technology,
		closeClearConfirm,
	} = params || {};
	const queryClient = useQueryClient();

	const {
		mutate: updateCell4GFddHandler,
		isLoading: isLoadingUpdateCell4GFdd,
	} = useMutation<IUpdateCellLimenetDTO>({
		mutationKey: [UPDATE_CELL_4G_FDD],
		mutationFn: updateCell4GFddLimenet,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updatedCell,
				type: ESnackbarTypes.success,
			}));

			closeCellModal();

			return queryClient.invalidateQueries([CROWDCELLS_CONFIG_DATA]);
		},
	});

	const {
		mutate: updateCell4GTddHandler,
		isLoading: isLoadingUpdateCell4GTdd,
	} = useMutation<IUpdateCellLimenetDTO>({
		mutationKey: [UPDATE_CELL_4G_TDD],
		mutationFn: updateCell4GTddLimenet,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updatedCell,
				type: ESnackbarTypes.success,
			}));

			closeCellModal();

			return queryClient.invalidateQueries([CROWDCELLS_CONFIG_DATA]);
		},
	});

	const {
		mutate: updateCell5GHandler,
		isLoading: isLoadingUpdateCell5G,
	} = useMutation<IUpdateCellLimenetDTO>({
		mutationKey: [UPDATE_CELL_5G],
		mutationFn: updateCell5GLimenet,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updatedCell,
				type: ESnackbarTypes.success,
			}));

			closeCellModal();

			return queryClient.invalidateQueries([CROWDCELLS_CONFIG_DATA]);
		},
	});

	const {
		mutate: handleClearCell,
		isLoading: isLoadingClearCell,
	} = useMutation<TCleanCellLimenetDTO>({
		mutationKey: [REMOVE_CELL],
		mutationFn: ({ id, cellId, timestamp }: TCleanCellLimenetDTO) => {
			switch (technology) {
				case ETechnologiesValues.C_4G_TDD:
					return cleanCell4GTddLimenet({ id, cellId, timestamp });
				case ETechnologiesValues.C_4G_FDD:
					return cleanCell4GFddLimenet({ id, cellId, timestamp });
				case ETechnologiesValues.C_5G:
					return cleanCell5GLimenet({ id, cellId, timestamp });
				default:
					return;
			}
		},
		onSuccess: () => {
			closeClearConfirm();

			dispatch(setSnackbar({
				message: responseMessages.clearCell,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([CROWDCELLS_CONFIG_DATA]);
		},
		onError: (err: IResponseError) => {
			closeClearConfirm();

			handleErrorFromMessage(err);
		},
	});

	return {
		isLoadingForm: isLoadingUpdateCell4GFdd || isLoadingUpdateCell4GTdd || isLoadingUpdateCell5G,
		isLoadingCellTable: isLoadingClearCell,
		handleClearCell,
		updateCell5GHandler,
		updateCell4GFddHandler,
		updateCell4GTddHandler,
	};
};
